import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useVoiceRecording } from '@/hooks/useVoiceRecording';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { logger } from '@/lib/logger';
import { useSiteSelector } from '@/hooks/useSiteSelector';
import { useCommandeFormules } from '@/hooks/useCommandeFormules';
import { useCommandeGlobaleOptions } from '@/hooks/useCommandeGlobaleOptions';
import { useSitePrevisionnel } from '@/hooks/useSitePrevisionnel';
import { Mic, MicOff, Loader2, Check, X, RefreshCw, AlertCircle, ChevronRight, Settings2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { VoiceOrderQuestionnaire, type VoiceOrderCompleteData, type ParsedOuvrage } from './VoiceOrderQuestionnaire';
import { VoiceOrderSummary } from './VoiceOrderSummary';

const MIN_RECORDING_SECONDS = 4;

type ModalState = 
  | 'idle' 
  | 'recording' 
  | 'transcribing' 
  | 'parsing' 
  | 'review' 
  | 'questionnaire' 
  | 'summary' 
  | 'success'
  | 'error';

interface ParsedOrder {
  workItems: ParsedOuvrage[];
  formula?: {
    name: string;
    strength?: string;
    exposure_class?: string;
    consistency?: string;
  };
  options?: string[];
  heure?: string;
  date?: string;
  comment?: string;
  confidence: number;
}

interface VoiceOrderModalContentProps {
  onOpenChange: (open: boolean) => void;
  onOrderParsed?: (order: ParsedOrder, transcription: string) => void;
}

export const VoiceOrderModalContent: React.FC<VoiceOrderModalContentProps> = ({
  onOpenChange,
  onOrderParsed,
}) => {
  const { selectedSite } = useSiteSelector();
  const { formules } = useCommandeFormules(selectedSite?.id || '');
  const { options: globalOptions } = useCommandeGlobaleOptions(selectedSite?.id || '');
  const { buildings } = useSitePrevisionnel(selectedSite?.id || '');
  
  // useVoiceRecording est appelé ICI, uniquement quand le contenu est rendu (= modal ouvert)
  const {
    isRecording,
    audioBlob,
    audioBase64,
    error: recordingError,
    duration,
    silenceWarning,
    availableDevices,
    selectedDeviceId,
    startRecording,
    stopRecording,
    resetRecording,
    setSelectedDevice,
  } = useVoiceRecording();

  const [showDeviceSelector, setShowDeviceSelector] = useState(false);
  const [modalState, setModalState] = useState<ModalState>('idle');
  const [transcription, setTranscription] = useState<string>('');
  const [parsedOrder, setParsedOrder] = useState<ParsedOrder | null>(null);
  const [completeOrderData, setCompleteOrderData] = useState<VoiceOrderCompleteData | null>(null);
  const [error, setError] = useState<string>('');

  // Handle recording state changes
  useEffect(() => {
    if (isRecording) {
      setModalState('recording');
    }
  }, [isRecording]);

  // Process audio when recording stops
  useEffect(() => {
    if (audioBase64 && !isRecording && modalState === 'recording') {
      processAudio(audioBase64);
    }
  }, [audioBase64, isRecording, modalState]);

  // Handle recording errors
  useEffect(() => {
    if (recordingError) {
      setError(recordingError);
      setModalState('error');
    }
  }, [recordingError]);

  const processAudio = async (audio: string) => {
    try {
      const audioBytes = audioBlob?.size || 0;

      // Step 1: Transcribe
      setModalState('transcribing');
      
      const { data: transcribeData, error: transcribeError } = await supabase.functions.invoke('voice-transcribe', {
        body: { audio, mimeType: 'audio/webm' }
      });

      if (transcribeError) throw new Error(transcribeError.message);
      if (transcribeData?.error) throw new Error(transcribeData.error);
      if (!transcribeData?.text) throw new Error('Aucune transcription reçue');

      setTranscription(transcribeData.text);

      // Step 2: Parse the order
      setModalState('parsing');

      // Build available work items from site prévisionnel
      const availableWorkItems = buildings.flatMap(building =>
        building.customWorkItems.flatMap(ouvrage =>
          building.levels
            .filter(niveau => (building.volumes[niveau]?.[ouvrage.id] || 0) > 0)
            .map(niveau => ({
              name: ouvrage.name,
              building: building.name,
              level: niveau,
              volume: building.volumes[niveau]?.[ouvrage.id] || 0,
            }))
        )
      );

      const context = {
        workItems: availableWorkItems,
        formulas: formules.map(f => ({
          name: f.name,
          strength: f.resistance,
          exposure_class: f.classe_exposition,
          consistency: f.consistance,
        })),
        options: globalOptions
          .filter(o => o.category === 'options_concrete_pour' && o.active)
          .map(o => o.name),
      };

      const { data: parseData, error: parseError } = await supabase.functions.invoke('parse-concrete-order', {
        body: { text: transcribeData.text, context }
      });

      if (parseError) throw new Error(parseError.message);
      if (!parseData?.success) throw new Error(parseData?.error || 'Erreur de parsing');

      setParsedOrder(parseData.order);
      setModalState('review');

    } catch (err) {
      logger.error("VoiceOrderModalContent", "Error processing audio:", err);
      setError(err instanceof Error ? err.message : 'Erreur lors du traitement');
      setModalState('error');
    }
  };

  const handleStartRecording = async () => {
    setError('');
    await startRecording();
  };

  const handleStopRecording = () => {
    stopRecording();
  };

  const handleRetry = () => {
    setModalState('idle');
    setTranscription('');
    setParsedOrder(null);
    setCompleteOrderData(null);
    setError('');
    resetRecording();
  };

  const handleContinueToQuestionnaire = () => {
    if (parsedOrder) {
      setModalState('questionnaire');
    }
  };

  const handleQuestionnaireComplete = (data: VoiceOrderCompleteData) => {
    setCompleteOrderData(data);
    setModalState('summary');
  };

  const handleBackToReview = () => {
    setModalState('review');
  };

  const handleBackToQuestionnaire = () => {
    setModalState('questionnaire');
  };

  const handleOrderSuccess = () => {
    setModalState('success');
    setTimeout(() => {
      onOpenChange(false);
    }, 2000);
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Idle State
  if (modalState === 'idle') {
    return (
      <div className="flex flex-col items-center gap-xl text-center py-2xl px-lg">
        <div className="w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center">
          <Mic className="w-12 h-12 text-primary" />
        </div>
        <div>
          <h3 className="text-title mb-2">Dicter une commande</h3>
          <p className="text-muted-foreground text-body-md max-w-xs">
            Appuyez sur le bouton et dictez votre commande de bétonnage. Par exemple :
          </p>
          <p className="text-helper text-muted-foreground mt-2 italic">
            "Je veux couler 10m³ dans les massifs du bâtiment A, niveau 1, avec du C25/30 S4 et un PUMI à 9h"
          </p>
          <p className="text-helper text-muted-foreground/70 mt-md">
            Parlez au moins 3 secondes pour une meilleure reconnaissance
          </p>
        </div>
        
        {/* Microphone selector */}
        {availableDevices.length > 1 && (
          <div className="w-full max-w-xs">
            <div className="flex items-center gap-sm mb-2">
              <Settings2 className="w-4 h-4 text-muted-foreground" />
              <span className="text-body-md text-muted-foreground">Microphone</span>
            </div>
            <Select
              value={selectedDeviceId || ''}
              onValueChange={setSelectedDevice}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Sélectionner un microphone" />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectLabel>Microphone</SelectLabel>
                  {availableDevices.map((device) => (
                    <SelectItem key={device.deviceId} value={device.deviceId}>
                      {device.label}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
        )}
        
        <Button size="lg" onClick={handleStartRecording} className="gap-sm">
          <Mic className="w-5 h-5" />
          Commencer l'enregistrement
        </Button>
      </div>
    );
  }

  // Recording State
  if (modalState === 'recording') {
    return (
      <div className="flex flex-col items-center gap-xl text-center py-2xl px-lg">
        <div className="relative">
          <div className="w-24 h-24 rounded-full bg-destructive/20 flex items-center justify-center animate-pulse">
            <Mic className="w-12 h-12 text-destructive" />
          </div>
          <div className="absolute -inset-4 rounded-full border-4 border-destructive/30 animate-ping" />
        </div>
        <div>
          <h3 className="text-title mb-1">Enregistrement en cours...</h3>
          <p className="text-value font-mono text-destructive">{formatDuration(duration)}</p>
          {duration < MIN_RECORDING_SECONDS && (
            <p className="text-helper text-muted-foreground mt-2">
              Minimum {MIN_RECORDING_SECONDS} secondes
            </p>
          )}
        </div>
        <Button 
          size="lg" 
          variant="destructive" 
          onClick={handleStopRecording} 
          className="gap-sm"
          disabled={duration < MIN_RECORDING_SECONDS}
        >
          <MicOff className="w-5 h-5" />
          {duration < MIN_RECORDING_SECONDS 
            ? `Patientez... ${MIN_RECORDING_SECONDS - duration}s`
            : "Arrêter l'enregistrement"}
        </Button>
      </div>
    );
  }

  // Transcribing State
  if (modalState === 'transcribing') {
    return (
      <div className="flex flex-col items-center gap-xl text-center py-2xl px-lg">
        <div className="w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center">
          <Loader2 className="w-12 h-12 text-primary animate-spin" />
        </div>
        <div>
          <h3 className="text-title mb-2">Transcription en cours...</h3>
          <p className="text-muted-foreground text-body-md">
            Conversion de votre audio en texte
          </p>
          {silenceWarning && (
            <div className="mt-lg p-md bg-amber-500/10 border border-amber-500/30 rounded-lg">
              <p className="text-amber-600 text-helper flex items-center gap-sm">
                <AlertCircle className="w-4 h-4" />
                Aucun son détecté - vérifiez votre microphone
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Parsing State
  if (modalState === 'parsing') {
    return (
      <div className="flex flex-col items-center gap-xl text-center py-2xl px-lg">
        <div className="w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center">
          <Loader2 className="w-12 h-12 text-primary animate-spin" />
        </div>
        <div>
          <h3 className="text-title mb-2">Analyse de la commande...</h3>
          <p className="text-muted-foreground text-body-md">
            Extraction des informations de bétonnage
          </p>
          {transcription && (
            <p className="text-helper text-muted-foreground mt-lg italic max-w-sm">
              "{transcription}"
            </p>
          )}
        </div>
      </div>
    );
  }

  // Review State
  if (modalState === 'review' && parsedOrder) {
    return (
      <div className="flex flex-col gap-xl w-full max-w-md py-2xl px-lg">
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-lg">
            <Check className="w-8 h-8 text-green-600" />
          </div>
          <h3 className="text-title">Commande analysée</h3>
          <p className="text-helper text-muted-foreground mt-1 italic">
            "{transcription}"
          </p>
        </div>

        {/* Parsed order preview */}
        <div className="bg-muted/50 rounded-lg p-lg space-y-md">
          {parsedOrder.workItems?.length > 0 && (
            <div>
              <span className="text-body-sm text-muted-foreground">Ouvrages</span>
              <div className="flex flex-wrap gap-sm mt-1">
                {parsedOrder.workItems.map((o, i) => (
                  <Badge key={i} variant="secondary">
                    {o.name} {o.building && `(${o.building}`}{o.level && `, N${o.level}`}{o.building && ')'} - {o.volume}m³
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {parsedOrder.formula && (
            <div>
              <span className="text-body-sm text-muted-foreground">Formule</span>
              <div className="flex flex-wrap gap-sm mt-1">
                <Badge variant="outline">{parsedOrder.formula.name}</Badge>
                {parsedOrder.formula.strength && (
                  <Badge variant="outline">{parsedOrder.formula.strength}</Badge>
                )}
                {parsedOrder.formula.consistency && (
                  <Badge variant="outline">{parsedOrder.formula.consistency}</Badge>
                )}
              </div>
            </div>
          )}

          {parsedOrder.options && parsedOrder.options.length > 0 && (
            <div>
              <span className="text-body-sm text-muted-foreground">Options</span>
              <div className="flex flex-wrap gap-sm mt-1">
                {parsedOrder.options.map((opt, i) => (
                  <Badge key={i} variant="secondary">{opt}</Badge>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-lg">
            {parsedOrder.heure && (
              <div>
                <span className="text-body-sm text-muted-foreground">Heure</span>
                <p className="text-body-md">{parsedOrder.heure}</p>
              </div>
            )}
            {parsedOrder.date && (
              <div>
                <span className="text-body-sm text-muted-foreground">Date</span>
                <p className="text-body-md">{parsedOrder.date}</p>
              </div>
            )}
          </div>

          <div className="flex items-center gap-sm pt-2 border-t">
            <span className="text-helper text-muted-foreground">Confiance:</span>
            <Badge 
              variant={parsedOrder.confidence > 0.8 ? 'default' : parsedOrder.confidence > 0.5 ? 'secondary' : 'destructive'}
              className="text-body-sm"
            >
              {Math.round(parsedOrder.confidence * 100)}%
            </Badge>
          </div>
        </div>

        <div className="flex gap-md">
          <Button variant="outline" onClick={handleRetry} className="flex-1 gap-sm">
            <RefreshCw className="w-4 h-4" />
            Recommencer
          </Button>
          <Button onClick={handleContinueToQuestionnaire} className="flex-1 gap-sm">
            Continuer
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    );
  }

  // Questionnaire State
  if (modalState === 'questionnaire' && parsedOrder && selectedSite) {
    return (
      <div className="py-lg px-lg">
        <VoiceOrderQuestionnaire
          parsedOrder={parsedOrder}
          siteId={selectedSite.id}
          onComplete={handleQuestionnaireComplete}
          onBack={handleBackToReview}
        />
      </div>
    );
  }

  // Summary State
  if (modalState === 'summary' && completeOrderData && selectedSite) {
    return (
      <div className="py-lg px-lg">
        <VoiceOrderSummary
          orderData={completeOrderData}
          siteId={selectedSite.id}
          onBack={handleBackToQuestionnaire}
          onSuccess={handleOrderSuccess}
        />
      </div>
    );
  }

  // Success State
  if (modalState === 'success') {
    return (
      <div className="flex flex-col items-center gap-xl text-center py-2xl px-lg">
        <div className="w-24 h-24 rounded-full bg-green-100 flex items-center justify-center">
          <Check className="w-12 h-12 text-green-600" />
        </div>
        <div>
          <h3 className="text-title mb-2">Commande créée !</h3>
          <p className="text-muted-foreground text-body-md">
            Votre commande a été enregistrée avec succès
          </p>
        </div>
      </div>
    );
  }

  // Error State
  if (modalState === 'error') {
    return (
      <div className="flex flex-col items-center gap-xl text-center py-2xl px-lg">
        <div className="w-24 h-24 rounded-full bg-destructive/10 flex items-center justify-center">
          <X className="w-12 h-12 text-destructive" />
        </div>
        <div>
          <h3 className="text-title mb-2">Erreur</h3>
          <p className="text-muted-foreground text-body-md max-w-xs">
            {error || 'Une erreur est survenue'}
          </p>
        </div>
        <Button onClick={handleRetry} className="gap-sm">
          <RefreshCw className="w-4 h-4" />
          Réessayer
        </Button>
      </div>
    );
  }

  return null;
};
